/**
 * Trust tiering — the safety core (2026-06-18 decision, refined this session).
 * Pure: takes a `hasDeterministic` predicate instead of the store, so the rule is
 * unit-testable in isolation. The actual cross-model (Codex) / self re-check is
 * agent-mediated and arrives as a verdict on each RawProposal.
 *
 *   deterministic core already has the same triple  -> HIGH (corroborated)
 *   genuinely new + a DIFFERENT model agreed         -> MEDIUM
 *   genuinely new + same model self-recheck agreed   -> LOW
 *   verifier disagreed                               -> rejected
 *
 * HIGH is EXACT canonical-triple equality with a deterministic relation — never
 * "a wikilink/prose suggests it". A HIGH (corroborated) proposal does NOT modify
 * the deterministic row in `relations`; it is recorded only as a proposal.
 */

import { TRUST_RANK } from "./types.js";
import type { RawProposal, RelationProposal, SourceKind, Trust, ValidatedProposal } from "./types.js";

export function tierProposal(
  v: ValidatedProposal,
  raw: Pick<RawProposal, "verifier" | "agreed">,
  hasDeterministic: (relId: string) => boolean,
  opts?: {
    readonly sourceRecordHash?: string | null;
    readonly proposedAt?: string | null;
    /** Phase-2: defaults to "record". "conversation" caps trust at MEDIUM. */
    readonly sourceKind?: SourceKind;
    readonly sourceFamily?: string | null;
    readonly extractionRunId?: string | null;
  },
): { ok: true; value: RelationProposal } | { ok: false; reason: "verifier-disagreed" } {
  const sourceKind: SourceKind = opts?.sourceKind ?? "record";
  const base = {
    ...v,
    sourceRecordHash: opts?.sourceRecordHash ?? null,
    proposedAt: opts?.proposedAt ?? null,
    confirmed: null,
    sourceKind,
    sourceFamily: opts?.sourceFamily ?? null,
    extractionRunId: opts?.extractionRunId ?? null,
  };

  // A verifier-disagreed proposal is rejected FIRST — even if a deterministic
  // triple matches. If the relation is genuinely deterministic it is already in the
  // trusted graph; recording a doubted proposal as "HIGH corroborated" would only
  // muddy provenance and contradict the "disagreed → rejected" rule (Codex round 2).
  if (!raw.agreed) return { ok: false, reason: "verifier-disagreed" };

  let trust: Trust;
  let verifiedBy: RelationProposal["verifiedBy"];
  if (hasDeterministic(v.relId)) {
    trust = "high";
    verifiedBy = "deterministic";
  } else if (raw.verifier === "cross-model") {
    trust = "medium";
    verifiedBy = "cross-model";
  } else {
    trust = "low";
    verifiedBy = "self";
  }

  // Phase-2 ceiling: a conversation-sourced proposal can never be HIGH on its own.
  // A "matching" deterministic record may be a summary derived FROM this very
  // conversation, so the corroboration could be circular — cap at MEDIUM and leave
  // HIGH to human confirmation (Codex review 2026-06-19 #3). verifiedBy keeps the
  // honest reason ("deterministic"); only the surfacing trust is clamped.
  if (sourceKind === "conversation" && trust === "high") {
    trust = "medium";
  }

  return { ok: true, value: { ...base, trust, trustRank: TRUST_RANK[trust], verifiedBy } };
}
